import React, { useState, useEffect, useCallback, useRef } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import PromptOpt from "./Components/PromptOpt";
import Button from "../Tools/Components/Button";
import { useTranslation } from 'react-i18next';

const PromptOptsPagina = () => {
	const [pageNr, setPageNr] = useState(1);
	const [entriesPerPage, setEntriesPerPage] = useState(25);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [promptOpts, setPromptOpts] = useState([]);
	const [searchString, setSearchString] = useState("");
	const { t } = useTranslation();

	const axiosRef = useRef(null);

	useEffect(() => {
		const httpService = HttpService;
		httpService.configure();
		axiosRef.current = httpService.getAxiosClient();

		cyclePage(0);
	}, []);

	const cyclePage = useCallback((nrToCycle = 0, overridePage = null, overrideSearch = null, overrideEntries = null) => {
		const axios = axiosRef.current;
		if (!axios) return;

		const newPageNr = overridePage ?? (pageNr + nrToCycle);
		const currentSearch = overrideSearch ?? searchString;
		const currentEntries = overrideEntries ?? entriesPerPage;

		setPageNr(newPageNr);

		axios.get(
			process.env.REACT_APP_INKOFACT_API_URL +
			`prompt/?entries=${currentEntries}&page=${newPageNr}&searchString=${currentSearch}`
		)
			.then(response => {
				setPromptOpts(response.data.collection);
				setHasNextPage(response.data.hasNextPage);
			})
			.catch(() => {
				setPromptOpts([]);
			});
	}, [pageNr, searchString, entriesPerPage]);

	const onSearchChangeHandler = (value) => {
		setSearchString(value);
		setPageNr(1);
		cyclePage(0, 1, value, entriesPerPage);
	};

	const onEntriesChangeHandler = (value) => {
		setEntriesPerPage(value);
		setPageNr(1);
		cyclePage(0, 1, searchString, value);
	};

	const PageButton = (text, nrToCycle) => (
		<div className="w-[42px] h-[23px] relative">
			<button
				className="bg-[#dedede] dark:bg-[#3F3F46] border-2 border-black rounded-full w-full h-full flex items-center justify-center"
				onClick={() => cyclePage(nrToCycle)}
			>
				<span className="text-sm font-medium">{text}</span>
			</button>
		</div>
	);

	const PageEntriesBox = () => (
		<div className="w-[56px] h-[23px] relative">
			<div className="bg-white border border-black rounded-sm w-full h-full flex items-center justify-center">
				<input
					type="number"
					onChange={e => onEntriesChangeHandler(e.target.value)}
					className="dark:bg-gray-700 w-[calc(100%-4px)] h-[calc(100%-4px)] text-center text-sm outline-none"
					value={entriesPerPage}
				/>
			</div>
		</div>
	);

	const PageCycleFrame = () => {
		if (searchString !== "") return null;

		return (
			<div className="flex items-center gap-[10px]">
				<span className="text-sm font-medium">{t("promptOpts.pageSizeLabel")}</span>
				{PageEntriesBox()}
				{pageNr > 1 && PageButton("<<", -1)}
				{hasNextPage && PageButton(">>", 1)}
			</div>
		);
	};

	return (
		<div className="relative size-full">
			<div className="flex flex-row size-full gap-5">

				{/* LEFT TOOL PANEL */}
				<div className="w-100 py-2">
					<div className="flex flex-col items-center size-full relative">
						<div className="absolute inset-0" />
						<div className="flex flex-col w-[364px] dark:bg-[rgba(55,65,81,0.75)]">
							<div className="h-[80px] bg-[#00aaad] dark:bg-[#008b8e] border-2 border-[#b0b6c1] flex items-center justify-center">
								<div className="capitalize font-bold text-[16px]">{t("inkofactToolsBox.header")}</div>
							</div>
							<div className="bg-[rgba(243,244,246,0.75)] border-2 border-t-0 border-[#b0b6c1] flex flex-col items-center gap-4 p-6">
								<Button text={t("inkofactToolsBox.kvkBtn")} onClick="/FactuurKvk" />
								<Button text={t("inkofactToolsBox.referentieBtn")} onClick="/FactuurReferentie" />
							</div>
						</div>
					</div>
				</div>

				{/* MAIN CONTENT */}
				<div className="flex flex-col items-center size-full py-2">

					{/* HEADER */}
					<div className="bg-[#00aaad] dark:bg-[#008b8e] w-full flex justify-center py-[10px] border-2 border-[#b0b6c1]">
						<div className="font-bold text-[20px] py-3">
							{t("promptOpts.header")}
						</div>
					</div>

					{/* CONTENT */}
					<div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border-2 border-[#b0b6c1]">
						<div className="flex flex-col items-center px-[28px] py-[10px] gap-[10px]">

							{/* SEARCH + BUTTON */}
							<div className="flex justify-between w-full h-[23px]">
								<div className="flex-row">
								<label className="font-bold px-[2px]">{t("promptOpts.searchLabel")}:</label>
								<input
									className="dark:bg-gray-700 w-[320px] px-2 border border-black"
									onChange={e => onSearchChangeHandler(e.target.value)}
									placeholder={t("promptOpts.searchPlaceholder")}
								/>
								</div>
								<Button text={t("promptOpts.newBtn")} onClick={"/PromptOpt/create"} height="23" width="290" />
							</div>

							{/* TABLE */}
							<div className="flex flex-col w-full">
								<div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(101,191,255,0.5)] h-[35px] flex items-center px-[10px] font-bold border border-black">
									<div className="flex-1">{t("promptOpts.senderTableHeader")}</div>
									<div className="flex-1">{t("promptOpts.receiverTableHeader")}</div>
								</div>

								{promptOpts.map((promptOpt, index) => (
									<PromptOpt key={promptOpt.id} promptOpt={promptOpt} index={index} />
								))}
							</div>

							{PageCycleFrame()}

						</div>
					</div>

				</div>
			</div>
		</div>
	);
};

export default PromptOptsPagina;